import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input, Select, Tag, Modal, Dropdown, Menu, message, Space, Tooltip } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  UndoOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useAssets, useAssetActions } from '../../../hooks/useAssets';
import { Asset, AssetStatus } from '../../../types/asset.types';
import { all_routes } from '../../../router/all_routes';
import CreateAssetModal from './components/CreateAssetModal';
import AssignAssetModal from './components/AssignAssetModal';
import EditAssetModal from './components/EditAssetModal';
import UpdateAssetModal from './components/UpdateAssetModal';
import ViewFilesModal from './components/ViewFilesModal';

const { Option } = Select;
const { confirm } = Modal;

const AssetsPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;

  const {
    assets,
    filteredAssets,
    employees,
    loading,
    error,
    selectedStatus,
    selectedEmployee,
    searchTerm,
    fetchAssets,
    fetchEmployees,
    handleStatusChange,
    handleEmployeeChange,
    handleSearchChange,
    resetFilters
  } = useAssets();

  const { deleteAsset, assignAsset, returnAsset } = useAssetActions();

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [viewFilesModalVisible, setViewFilesModalVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const assignableEmployess = useMemo(() => {
    return employees.filter((employee) =>
      employee.status?.toString().trim().toLowerCase() !== "resigned");
  }, [employees])

  console.log(assignableEmployess)

  useEffect(() => {
    fetchAssets();
    fetchEmployees();
  }, [fetchAssets, fetchEmployees]);

  const handleDelete = async (assetId: number) => {
    try {
      await deleteAsset(assetId);
      fetchAssets(); // Listeyi yenile
    } catch (error) {
      // console.error('Delete failed:', error);
    }
  };

  const showDeleteConfirm = (asset: Asset) => {
    confirm({
      title: 'Zimmeti silmek istediğinize emin misiniz?',
      icon: <ExclamationCircleOutlined />,
      content: `${asset.name} (${asset.serialNumber}) kalıcı olarak silinecek.`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk() {
        handleDelete(asset.id);
      },
    });
  };

  const handleAssignAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setAssignModalVisible(true);
  };

  const handleEditAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setEditModalVisible(true);
  };

  const handleStatusUpdate = (asset: Asset) => {
    setSelectedAsset(asset);
    setUpdateModalVisible(true);
  };

  const handleReturnAsset = async (asset: Asset) => {
    try {
      await returnAsset(asset.id);
      fetchAssets();
    } catch (error) {
      // console.error('Return asset failed:', error);
    }
  };

  const handleViewFiles = (asset: Asset) => {
    setSelectedAsset(asset);
    setViewFilesModalVisible(true);
  };

  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'success';
      case AssetStatus.ASSIGNED:
        return 'processing';
      case AssetStatus.SCRAPPED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'Envanterde';
      case AssetStatus.ASSIGNED:
        return 'Atanmış';
      case AssetStatus.SCRAPPED:
        return 'Kullanım Dışı';
      default:
        return status;
    }
  };

  const getEmployeeName = (asset: Asset) => {
    if (asset.user) {
      return `${asset.user.firstName} ${asset.user.lastName}`;
    }
    return '-';
  };

  const actionMenu = (record: Asset) => (
    <Menu>
      <Menu.Item key="editInfo" icon={<EditOutlined />} onClick={() => handleEditAsset(record)}>
        Bilgileri Düzenle
      </Menu.Item>
      <Menu.Divider />
      {record.status === AssetStatus.IN_STOCK && (
        <Menu.Item key="assign" icon={<SendOutlined />} onClick={() => handleAssignAsset(record)}>
          Çalışana Ata
        </Menu.Item>
      )}
      {record.status === AssetStatus.ASSIGNED && (
        <Menu.Item key="return" icon={<UndoOutlined />} onClick={() => handleReturnAsset(record)}>
          İade Al
        </Menu.Item>
      )}
      <Menu.Item key="status" icon={<EditOutlined />} onClick={() => handleStatusUpdate(record)}>
        Durum Güncelle
      </Menu.Item>
      {record.files && record.files.length > 0 && (
        <Menu.Item key="files" icon={<FileTextOutlined />} onClick={() => handleViewFiles(record)}>
          Dosyaları Görüntüle ({record.files.length})
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => showDeleteConfirm(record)} danger>
        Sil
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Zimmet Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Asset, b: Asset) => a.name.localeCompare(b.name),
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      sorter: (a: Asset, b: Asset) => a.serialNumber.localeCompare(b.serialNumber),
    },
    {
      title: 'Çalışan',
      dataIndex: 'user',
      key: 'user',
      render: (user: any, record: Asset) => (
        <div>
          {getEmployeeName(record)}
          {record.user && (
            <div className="small text-muted">{record.user.email}</div>
          )}
        </div>
      ),
      sorter: (a: Asset, b: Asset) => getEmployeeName(a).localeCompare(getEmployeeName(b)),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      sorter: (a: Asset, b: Asset) => a.status.localeCompare(b.status),
    },
    {
      title: 'Dosyalar',
      dataIndex: 'files',
      key: 'files',
      render: (files: any[], record: Asset) => (
        <div>
          {files && files.length > 0 ? (
            <Tooltip title={`${files.length} dosya mevcut - Görüntülemek için tıklayın`}>
              <Button
                type="text"
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => handleViewFiles(record)}
                style={{
                  color: '#1890ff',
                  fontWeight: '500'
                }}
              >
                {files.length}
              </Button>
            </Tooltip>
          ) : (
            <span className="text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'Oluşturma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <div>
          <div>{new Date(date).toLocaleDateString('tr-TR')}</div>
          <div className="small text-muted">{new Date(date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      ),
      sorter: (a: Asset, b: Asset) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (text: any, record: Asset) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']}>
          <Button type="link">İşlemler</Button>
        </Dropdown>
      ),
    },
  ];

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    fetchAssets();
  };

  const handleAssignSuccess = () => {
    setAssignModalVisible(false);
    setSelectedAsset(null);
    fetchAssets();
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedAsset(null);
    fetchAssets();
  };

  const handleUpdateSuccess = () => {
    setUpdateModalVisible(false);
    setSelectedAsset(null);
    fetchAssets();
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-header-title">
          <h4 className="fw-medium mb-0">Zimmet Yönetimi</h4>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={routes.adminDashboard}>Anasayfa</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Zimmet Yönetimi
              </li>
            </ol>
          </nav>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="card-body">
            <div className="row g-3 align-items-center">
              <div className="col-md-3">
                <Input
                  placeholder="Zimmet adı veya seri numarası ara..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  allowClear
                />
              </div>
              <div className="col-md-3">
                <Select
                  placeholder="Durum seçin"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="all">Tüm Durumlar</Option>
                  <Option value={AssetStatus.IN_STOCK}>Envanterde</Option>
                  <Option value={AssetStatus.ASSIGNED}>Atanmış</Option>
                  <Option value={AssetStatus.SCRAPPED}>Kullanım Dışı</Option>
                </Select>
              </div>
              <div className="col-md-3">
                <Select
                  placeholder="Çalışan seçin"
                  value={selectedEmployee}
                  onChange={handleEmployeeChange}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">Tüm Çalışanlar</Option>
                  {assignableEmployess.map(assingnableemployee => (
                    <Option key={assingnableemployee.id} value={assingnableemployee.id.toString()}>
                      {assingnableemployee.firstName} {assingnableemployee.lastName}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="col-md-6">
                <div className="d-flex flex-wrap gap-2">
                  <Button icon={<FilterOutlined />} onClick={resetFilters}>
                    Filtreleri Temizle
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    Yeni Zimmet
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="row">
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-primary rounded-circle">
                      <i className="ti ti-archive text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Toplam Zimmet</h6>
                    <h4 className="mb-0">{assets.length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-warning rounded-circle">
                      <i className="ti ti-user-check text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Atanmış</h6>
                    <h4 className="mb-0">{assets.filter(a => a.status === AssetStatus.ASSIGNED).length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-success rounded-circle">
                      <i className="ti ti-package text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Envanterde</h6>
                    <h4 className="mb-0">{assets.filter(a => a.status === AssetStatus.IN_STOCK).length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-danger rounded-circle">
                      <i className="ti ti-trash text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Kullanım Dışı</h6>
                    <h4 className="mb-0">{assets.filter(a => a.status === AssetStatus.SCRAPPED).length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <Table
              columns={columns}
              dataSource={filteredAssets}
              loading={loading}
              rowKey="id"
              pagination={{
                total: filteredAssets.length,
                pageSize: 10,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
              }}
            />
          </div>
        </div>

        {/* Modals */}
        <CreateAssetModal
          visible={createModalVisible}
          onClose={() => setCreateModalVisible(false)}
          onSuccess={handleCreateSuccess}
        />

        <AssignAssetModal
          visible={assignModalVisible}
          onClose={() => {
            setAssignModalVisible(false);
            setSelectedAsset(null);
          }}
          onSuccess={handleAssignSuccess}
          asset={selectedAsset}
          employees={assignableEmployess}
        />

        <EditAssetModal
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedAsset(null);
          }}
          onSuccess={handleEditSuccess}
          asset={selectedAsset}
        />

        <UpdateAssetModal
          visible={updateModalVisible}
          onClose={() => {
            setUpdateModalVisible(false);
            setSelectedAsset(null);
          }}
          onSuccess={handleUpdateSuccess}
          asset={selectedAsset}
        />

        <ViewFilesModal
          visible={viewFilesModalVisible}
          onClose={() => {
            setViewFilesModalVisible(false);
            setSelectedAsset(null);
          }}
          asset={selectedAsset}
        />
      </div>
    </div>
  );
};

export default AssetsPage; 